import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import logoBlack from '../../assets/logo-black.webp';
import logoWhite from '../../assets/logo-white.webp';
import { useModals } from '../../app/Modals';
import { NAV_ITEMS } from '../../app/navigation';
import { MobileNavDrawer } from './MobileNavDrawer';
import { LanguageSwitcher } from '../navigation/LanguageSwitcher';
import { LocaleLink } from '../navigation/LocaleLink';
import { NavDropdown } from '../navigation/NavDropdown';
import { SearchButton } from '../navigation/SearchButton';
import { ThemeToggle } from '../navigation/ThemeToggle';

/**
 * SiteHeader — spec §25.2. FIXED and overlaying the page so it can be transparent over the
 * Home hero and opaque everywhere else:
 *   - `overHero` && not scrolled → transparent, white text/logo over the hero photo
 *   - inner pages, or scrolled past the hero → solid panel, themed text/logo
 *
 * SiteLayout offsets non-hero pages with top padding so content clears the fixed bar; on a
 * hero page the full-bleed hero sits under the transparent header by design.
 *
 * Nav model (which items are links vs dropdown-only) lives in app/navigation.ts; the
 * dropdown panels themselves are NavDropdown (§5 table, §25.2).
 */
export function SiteHeader({ overHero = false }: { overHero?: boolean }) {
  const { t } = useTranslation();
  const modals = useModals();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll(); // sync on mount / route change
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const transparent = overHero && !scrolled;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'border-b border-line bg-panel/95 backdrop-blur'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6 lg:px-8">
        <LocaleLink to="/" className="shrink-0" aria-label="Visit Samtskhe-Javakheti — home">
          {transparent ? (
            // Over the dark hero the logo is always white, regardless of theme.
            <img src={logoWhite} alt="Visit Samtskhe-Javakheti" className="h-10 w-auto" />
          ) : (
            <>
              <img src={logoBlack} alt="Visit Samtskhe-Javakheti" className="h-10 w-auto dark:hidden" />
              <img src={logoWhite} alt="Visit Samtskhe-Javakheti" className="hidden h-10 w-auto dark:block" />
            </>
          )}
        </LocaleLink>

        <nav aria-label="Main" className="hidden flex-1 items-center gap-6 lg:flex">
          {NAV_ITEMS.map((item) =>
            item.children?.length ? (
              // Has a dropdown (§5 table) — NavDropdown handles both the link-parent case
              // (WHAT TO SEE) and the navigates-nowhere case.
              <NavDropdown key={item.labelKey} item={item} transparent={transparent} />
            ) : (
              <LocaleLink
                key={item.labelKey}
                to={item.href ?? '/'}
                end={item.href === '/'}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    transparent
                      ? isActive
                        ? 'text-white underline underline-offset-8'
                        : 'text-white/80 hover:text-white'
                      : isActive
                        ? 'text-accent underline underline-offset-8'
                        : 'text-content hover:text-accent'
                  }`
                }
              >
                {t(item.labelKey)}
              </LocaleLink>
            ),
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {/* Opens the membership modal — a modal, not a route (§5, §25.5). Reads as a
              dark pill over the hero and an ink/white pill on solid headers. */}
          <button
            type="button"
            onClick={() => modals.open('membership')}
            className={`hidden rounded-pill px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 sm:inline-flex ${
              transparent
                ? 'bg-ink text-white'
                : 'bg-ink text-white dark:bg-white dark:text-ink'
            }`}
          >
            {t('nav.becomeAMember')}
          </button>
          <SearchButton
            className={transparent ? 'border-white/30 text-white hover:bg-white/10' : ''}
          />
          <LanguageSwitcher
            className={transparent ? 'border-white/30 text-white hover:bg-white/10' : ''}
          />
          <ThemeToggle
            className={transparent ? 'border-white/30 text-white hover:bg-white/10' : ''}
          />

          {/* Hamburger — shown exactly where the desktop nav is hidden (§25.2). */}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={t('a11y.openMenu')}
            aria-expanded={menuOpen}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-pill border border-line text-content transition-colors hover:bg-content/5 lg:hidden ${
              transparent ? 'border-white/30 text-white hover:bg-white/10' : ''
            }`}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
              className="h-4 w-4"
            >
              <path d="M3 6h14M3 10h14M3 14h14" />
            </svg>
          </button>
        </div>
      </div>

      <MobileNavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </header>
  );
}
